<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { cva } from 'class-variance-authority';
import { setLocale } from '@/i18n';
import { LOCALE_NAMES, QURAN_LOCALES } from '@/utils/quran/locale';

const { locale, t } = useI18n();

const props = defineProps({
  showLabel: { type: Boolean, default: true },
  variant: {
    type: String,
    default: 'pills',
    validator: (v) => ['pills', 'select'].includes(v),
  },
});

const containerClass = computed(() =>
  cva(
    'inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-card',
    {
      variants: {
        intent: { default: '' },
      },
    }
  )({ intent: 'default' })
);

const pillClass = computed(() =>
  cva(
    'px-3 py-1.5 text-xs font-semibold rounded-xl transition select-none',
    {
      variants: {
        active: {
          true: 'bg-green-main text-white shadow-sm',
          false: 'text-slate-700 hover:bg-slate-100',
        },
      },
    }
  )
);

const selectClass = computed(() =>
  cva(
    'h-9 rounded-2xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-800 shadow-card outline-none focus:ring-4 focus:ring-green-light/20 focus:border-green-light',
    {
      variants: {
        tone: { default: '' },
      },
    }
  )({ tone: 'default' })
);

const current = computed({
  get: () => locale.value,
  set: (v) => setLocale(v),
});
</script>

<template>
  <div class="flex items-center gap-3">
    <div v-if="props.showLabel" class="text-xs font-medium text-slate-600">
      {{ t('legal.privacy.languageLabel') }}
    </div>

    <div v-if="props.variant === 'pills'" :class="containerClass" role="tablist" aria-label="Language">
      <button
        v-for="lang in QURAN_LOCALES"
        :key="lang"
        type="button"
        role="tab"
        :aria-selected="current === lang"
        :class="pillClass({ active: current === lang })"
        @click="current = lang"
      >
        {{ LOCALE_NAMES[lang] }}
      </button>
    </div>

    <select
      v-else
      v-model="current"
      :class="selectClass"
      aria-label="Language"
    >
      <option v-for="lang in QURAN_LOCALES" :key="lang" :value="lang">{{ LOCALE_NAMES[lang] }}</option>
    </select>
  </div>
</template>

